// @spec POL-STR-039
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { Plus, Search, Shield } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, type SelectOption } from '@/components/ui/select';
import {
  ComplianceFrameworkDialog,
  ComplianceFrameworksTable,
  useComplianceFrameworks,
  useCreateComplianceFramework,
  useDeleteComplianceFramework,
  useUpdateComplianceFramework,
} from '@/features/admin';
import type {
  ComplianceFramework,
  CreateComplianceFrameworkRequest,
  UpdateComplianceFrameworkRequest,
} from '@/features/admin';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/compliance-frameworks')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.complianceFrameworks'),
  }),
  component: ComplianceFrameworksPage,
});

type FrameworkTypeFilter = 'all' | 'global' | 'custom';

function getTypeFilterOptions(t: (key: string) => string): SelectOption[] {
  return [
    { value: 'all', label: t('complianceFrameworks.allTypes') },
    { value: 'global', label: t('complianceFrameworks.global') },
    { value: 'custom', label: t('complianceFrameworks.custom') },
  ];
}

function ComplianceFrameworksPage() {
  const { t } = useTranslation('settings');
  const TYPE_FILTER_OPTIONS = getTypeFilterOptions(t);
  const { isAdmin } = useOrgRole();

  // State for filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<FrameworkTypeFilter>('all');

  // State for dialogs
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingFramework, setEditingFramework] = useState<ComplianceFramework | null>(null);
  const [deletingFramework, setDeletingFramework] = useState<ComplianceFramework | null>(null);

  // API hooks - only pass search if it has a value
  const filtersForQuery = search
    ? { includeGlobal: true, includeInactive: true, search }
    : { includeGlobal: true, includeInactive: true };
  const { data: frameworksData, isLoading } = useComplianceFrameworks(filtersForQuery);
  const createMutation = useCreateComplianceFramework();
  const updateMutation = useUpdateComplianceFramework();
  const deleteMutation = useDeleteComplianceFramework();

  // Filter frameworks based on type filter
  const filteredFrameworks = useMemo(() => {
    const frameworks = frameworksData?.items ?? [];
    if (typeFilter === 'all') return frameworks;
    if (typeFilter === 'global') return frameworks.filter((f) => f.isGlobal);
    return frameworks.filter((f) => !f.isGlobal);
  }, [frameworksData?.items, typeFilter]);

  // Handlers
  const handleCreateOpen = () => {
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (framework: ComplianceFramework) => {
    setEditingFramework(framework);
  };

  const handleToggleActive = async (framework: ComplianceFramework) => {
    if (framework.isGlobal) {
      toast.error(t('settings:toast.cannotModifyGlobalFrameworks'));
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: framework.id,
        data: { isActive: !framework.isActive },
      });
      toast.success(framework.isActive ? i18n.t('settings:toast.frameworkDeactivated') : i18n.t('settings:toast.frameworkActivated'));
    } catch (error) {
      const message = error instanceof Error ? error.message : t('complianceFrameworks.failedToUpdateFramework');
      toast.error(message);
    }
  };

  const handleDelete = (framework: ComplianceFramework) => {
    setDeletingFramework(framework);
  };

  const handleCreateSubmit = async (data: CreateComplianceFrameworkRequest) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success(t('settings:toast.frameworkCreatedSuccessfully'));
      setIsCreateDialogOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('complianceFrameworks.failedToCreateFramework');
      toast.error(message);
      throw error;
    }
  };

  const handleUpdateSubmit = async (
    data: CreateComplianceFrameworkRequest | UpdateComplianceFrameworkRequest
  ) => {
    if (!editingFramework) return;
    try {
      // When editing, the dialog provides UpdateComplianceFrameworkRequest
      // Cast to the update type since we know we're in edit mode
      await updateMutation.mutateAsync({
        id: editingFramework.id,
        data: data as UpdateComplianceFrameworkRequest,
      });
      toast.success(t('settings:toast.frameworkUpdatedSuccessfully'));
      setEditingFramework(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('complianceFrameworks.failedToUpdateFramework');
      toast.error(message);
      throw error;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingFramework) return;
    try {
      await deleteMutation.mutateAsync(deletingFramework.id);
      toast.success(t('settings:toast.frameworkDeletedSuccessfully'));
      setDeletingFramework(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('complianceFrameworks.failedToDeleteFramework');
      toast.error(message);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <h2 className="text-lg font-medium text-destructive">{t('complianceFrameworks.accessDenied')}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('complianceFrameworks.accessDeniedDescription')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-8">
        <header className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-foreground">{t('complianceFrameworks.pageTitle')}</h1>
              <p className="mt-1 text-muted-foreground">
                {t('complianceFrameworks.pageDescription')}
              </p>
            </div>
          </div>

          <Button onClick={handleCreateOpen}>
            <Plus className="mr-2 h-4 w-4" />
            {t('complianceFrameworks.addFramework')}
          </Button>
        </header>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('complianceFrameworks.searchPlaceholder')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              className="pl-9"
            />
          </div>
          <div className="w-40">
            <Select
              options={TYPE_FILTER_OPTIONS}
              value={typeFilter}
              onChange={(value) => {
                setTypeFilter(value as FrameworkTypeFilter);
              }}
            />
          </div>
        </div>

        {/* Table */}
        <ComplianceFrameworksTable
          frameworks={filteredFrameworks}
          isLoading={isLoading}
          onEdit={handleEdit}
          onToggleActive={(framework) => {
            void handleToggleActive(framework);
          }}
          onDelete={handleDelete}
        />

        {/* Create Dialog */}
        <ComplianceFrameworkDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onSubmit={async (data) => {
            // The dialog passes CreateComplianceFrameworkRequest when no framework is provided
            await handleCreateSubmit(data as CreateComplianceFrameworkRequest);
          }}
          isSubmitting={createMutation.isPending}
        />

        {/* Edit Dialog */}
        {editingFramework && (
          <ComplianceFrameworkDialog
            open={!!editingFramework}
            onOpenChange={(open) => {
              if (!open) setEditingFramework(null);
            }}
            framework={editingFramework}
            onSubmit={handleUpdateSubmit}
            isSubmitting={updateMutation.isPending}
          />
        )}

        {/* Delete Confirmation Dialog */}
        {deletingFramework && (
          <Dialog
            open={!!deletingFramework}
            onOpenChange={(open) => {
              if (!open) setDeletingFramework(null);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('complianceFrameworks.deleteFramework')}</DialogTitle>
                <DialogDescription>
                  {t('complianceFrameworks.deleteFrameworkConfirm', { name: deletingFramework.name })}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeletingFramework(null);
                  }}
                  disabled={deleteMutation.isPending}
                >
                  {t('complianceFrameworks.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    void handleDeleteConfirm();
                  }}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? t('complianceFrameworks.deleting') : t('complianceFrameworks.delete')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
